import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react'
import Constants from '../../../app/utils/constants'
import FormField from '../../atoms/FormField/FormField'
import { toast } from 'react-toastify'
import NameIcon from '../../../assets/images/icons/name1.svg'
import EmailIcon from '../../../assets/images/icons/email1.svg'
import SubjectIcon from '../../../assets/images/icons/subject1.svg'
import number from '../../../assets/images/icons/number.svg'
import { trackClarityEvent } from "../../../app/utils/ClarityAnalytics";
import { trackEvent } from "../../../app/utils/firebaseEvents";
import { CLARITY_EVENTS } from "../../../app/utils/EventTypes";

type MessageThemeColor = 'purple' | 'green' | 'rabbitx' | 'arbventure' | 'tatvacaretheme' | 'ddxtheme' | 'medilearntheme'
type MessageVariant = 'default' | 'contact'

type MessageProps = {
  themeColor?: MessageThemeColor
  variant?: MessageVariant
  wrapperClassName?: string
}

const SCRIPT_URL = process.env.REACT_APP_CONTACT_FORM_ENDPOINT || ''

const orbColorMap: Record<MessageThemeColor, string> = {
  purple: 'rgba(114, 97, 209, 0.842)',
  green: 'rgba(34, 197, 94, 0.50)',
  rabbitx: 'rgba(239, 68, 68, 0.418)',
  arbventure: 'rgba(95, 153, 55, 0.55)',
  tatvacaretheme: 'rgba(232, 210, 248, 0.52)',
  ddxtheme: 'rgba(169, 91, 218, 0.897)',
  medilearntheme: 'rgb(215, 241, 153)',
}

const fieldThemeMap: Record<MessageThemeColor, MessageThemeColor> = {
  purple: 'purple',
  green: 'green',
  rabbitx: 'rabbitx',
  arbventure: 'arbventure',
  tatvacaretheme: 'tatvacaretheme',
  ddxtheme: 'ddxtheme',
  medilearntheme: 'medilearntheme',
}

const textareaBgMap: Record<MessageThemeColor, string> = {
  purple: 'bg-[#140f35]/70',
  green: 'bg-[#0f2d1f]/20',
  rabbitx: 'bg-[#2a0d0d89]/20',
  arbventure: 'bg-[#1f2a12]/70',
  tatvacaretheme: 'bg-[#e7a4e9ce]/20',
  ddxtheme: 'bg-[#6e3f92]/30',
  medilearntheme: 'bg-[#c4d899d8]/30',
}

const borderGradientMap: Record<MessageThemeColor, string> = {
  purple:     'linear-gradient(130deg, #7261d1c1, #1a191f76, #7261d1ca)',
  green:      'linear-gradient(130deg, #45c97576, #2129248d, #45c9757a)',
  rabbitx:    'linear-gradient(130deg, #ef444457, #2b222290, #ef44444e)',
  arbventure: 'linear-gradient(130deg, #5e993790, #151615, #5e993790)',
  tatvacaretheme:  'linear-gradient(130deg, rgba(232,180,240,0.55), rgba(232, 180, 240, 0.349), rgba(164,97,216,0.55))',
  ddxtheme: 'linear-gradient(130deg, rgba(155, 63, 212, 0.877), rgba(43,9,70,0.45), rgba(94,15,155,0.55))',
  medilearntheme: 'linear-gradient(130deg, rgba(155, 176, 105, 0.877), rgba(155, 176, 105, 0.877), rgba(155, 176, 105, 0.877)',
}
const cardBgMap: Record<MessageThemeColor, string> = {
  purple:     'bg-[#0F0A26]',
  green:      'bg-[#0a1f12]',
  rabbitx:    'bg-[#130d2bdf]',
  arbventure: 'bg-[#0e1a08]',
  tatvacaretheme:  'bg-[#A461D8]/70',
  ddxtheme: 'bg-[#5E0F9B]/50',
  medilearntheme: 'bg-[#94a76de1]/80', 
}


/* Glow Orb */
const WanderingOrb = ({ color }: { color: string }) => {
  const orbRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const orb = orbRef.current
    if (!orb) return

    let cx = 50, cy = 50
    let tx = 50, ty = 50

    const driftTarget = () => {
      tx += (Math.random() - 0.5) * 50
      ty += (Math.random() - 0.5) * 50
      tx = Math.max(10, Math.min(90, tx))
      ty = Math.max(10, Math.min(90, ty))
    }

    const SPEED = 0.01
    let raf: number
    let frame = 0

    const tick = () => {
      if (frame % 120 === 0) driftTarget()
      cx += (tx - cx) * SPEED
      cy += (ty - cy) * SPEED
      orb.style.left = `${cx}%`
      orb.style.top = `${cy}%`
      frame++
      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <div
      ref={orbRef}
      className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2"
      style={{
        width: '55%',
        height: '55%',
        borderRadius: '50%',
        background: color,
        filter: 'blur(90px)',
        opacity: 0.65,
      }}
    />
  )
}

const Message = ({
  themeColor = 'purple',
  variant = 'default',
  wrapperClassName = '',
}: MessageProps) => {
  const { messageSection } = Constants

  const [formData, setFormData] = useState<Record<string, string>>({
    name: '',
    email: '',
    number: '',
    subject: '',
    message: '',
  })

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    number: '',
    subject: '',
  })

  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isContactVariant = variant === 'contact'

  /* VALIDATION */
  const validateName = (v: string) =>
    !v.trim()
      ? 'Name is required'
      : !/^[A-Za-z\s]+$/.test(v)
      ? 'Only alphabets allowed'
      : ''

  const validateEmail = (v: string) =>
    !v.trim()
      ? 'Email is required'
      : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
      ? 'Invalid email'
      : ''

  const validateNumber = (v: string) =>
    !v.trim()
      ? 'Number is required'
      : !/^\d{10}$/.test(v)
      ? 'Must be 10 digits'
      : ''

  const validateSubject = (v: string) =>
    !v.trim()
      ? 'Subject is required'
      : !/^[A-Za-z\s]+$/.test(v)
      ? 'Only alphabets allowed'
      : ''

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const getButtonColor = () => {
    switch (themeColor) {
      case 'green':
        return '#22C55E'
      case 'rabbitx':
        return '#ef4444df'
      case 'arbventure':
        return '#5F9937'
      case 'tatvacaretheme':
      case 'ddxtheme':
      case 'medilearntheme':
        return '#ffffff'
      default:
        return '#7261D1'
    }
  }

  const getButtonTextClass = () =>
    themeColor === 'tatvacaretheme' || themeColor === 'ddxtheme' || themeColor === 'medilearntheme'
      ? 'text-black'
      : 'text-white'
  
  const getButtonSpinnerClass = () =>
    themeColor === 'tatvacaretheme' || themeColor === 'ddxtheme' || themeColor === 'medilearntheme'
      ? 'border-black/30 border-t-black'
      : 'border-white/30 border-t-white'

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitAttempted(true)

    const nameError = validateName(formData.name)
    const emailError = validateEmail(formData.email)
    const numberError = isContactVariant ? validateNumber(formData.number) : ''
    const subjectError = validateSubject(formData.subject)

    setErrors({
      name: nameError,
      email: emailError,
      number: numberError,
      subject: subjectError,
    })

    if (
      nameError ||
      emailError ||
      numberError ||
      subjectError
    ) return

    setIsSubmitting(true)

    try {
      const payload = {
        name: formData.name || '-',
        email: formData.email || '-',
        number: formData.number || '-',
        subject: formData.subject || '-',
        message: formData.message || '-',
      }

      await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      toast.success('Form submitted successfully.')

      setFormData({
        name: '',
        email: '',
        number: '',
        subject: '',
        message: '',
      })

      setSubmitAttempted(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Something went wrong while submitting the form.'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
  // Replace with this:
// Replace with this:
<div
  className={`p-[1px] rounded-3xl w-full ${wrapperClassName}`}
  style={{ background: borderGradientMap[themeColor] }}
>
<div className={`relative min-h-[650px] md:min-h-[610px] xl:min-h-[625px] 2xl:min-h-[650px] w-full h-full overflow-hidden rounded-3xl px-5 flex flex-col items-center justify-center ${cardBgMap[themeColor]}`}>

      <WanderingOrb color={orbColorMap[themeColor]} />

      {/* ✅ CONTENT BACK */}
      {isContactVariant ? (
        <div className="flex flex-col gap-1 relative z-10 mb-[4%]">
          <p className="font-gilroy font-normal text-center text-xl 2xl:text-2xl text-gray-400">
            Get in touch
          </p>
          <h3 className="text-3xl 2xl:text-4xl text-center font-rajdhani font-semibold text-white">
            Write us something
          </h3>
        </div>
      ) : (
        <div className="relative z-10 text-center mb-[4%]">
          <h3 className="text-3xl 2xl:text-4xl font-rajdhani font-semibold text-white">
            {messageSection.header}
          </h3>
          <p className="font-gilroy font-normal text-xl 2xl:text-2xl text-white/50">
            {messageSection.description}
          </p>
        </div>
      )}

      <form className="relative z-10 w-full max-w-xl flex flex-col gap-4 mt-5" onSubmit={handleSubmit}>

        <div>
          <FormField
            iconSrc={NameIcon}
            name="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={handleInputChange}
            themeColor={fieldThemeMap[themeColor]}
          />
          {submitAttempted && errors.name && <p className="mt-1 text-xs text-red-300 text-start">{errors.name}</p>}
        </div>

        <div>
          <FormField
            iconSrc={EmailIcon}
            name="email"
            placeholder="Your Email"
            value={formData.email}
            onChange={handleInputChange}
            themeColor={fieldThemeMap[themeColor]}
          />
          {submitAttempted && errors.email && <p className="mt-1 text-xs text-red-300 text-start">{errors.email}</p>}
        </div>

        {isContactVariant && (
          <div>
            <FormField
              iconSrc={number}
              name="number"
              placeholder="Your Number"
              value={formData.number}
              onChange={handleInputChange}
              themeColor={fieldThemeMap[themeColor]}
            />
            {submitAttempted && errors.number && <p className="mt-1 text-xs text-red-300 text-start">{errors.number}</p>}
          </div>
        )}

        <div>
          <FormField
            iconSrc={SubjectIcon}
            name="subject"
            placeholder="Subject"
            value={formData.subject}
            onChange={handleInputChange}
            themeColor={fieldThemeMap[themeColor]}
          />
          {submitAttempted && errors.subject && <p className="mt-1 text-xs text-red-300 text-start">{errors.subject}</p>}
        </div>

        <textarea
          name="message"
          placeholder="Message"
          className={`p-4 rounded-xl ${textareaBgMap[themeColor]} text-white`}
          value={formData.message}
          onChange={handleInputChange}
        />

       <button
  type="submit"
  disabled={isSubmitting}
  onClick={() => {
    const payload = {
      page: window.location.pathname,
      section: 'contact_form',
    };
    trackClarityEvent(CLARITY_EVENTS.SEND_MESSAGE, payload);
    trackEvent(CLARITY_EVENTS.SEND_MESSAGE, payload);
  }}
  className={`inline-flex w-full items-center justify-center gap-3 rounded-xl px-8 py-3 font-semibold mt-6 ${getButtonTextClass()} ${isSubmitting ? 'opacity-80 cursor-not-allowed' : ''}`}
  style={{ backgroundColor: getButtonColor() }}
>
  <span className="inline-flex items-center gap-3">
    {isSubmitting && (
      <span className={`h-5 w-5 animate-spin rounded-full border-2 ${getButtonSpinnerClass()}`} />
    )}
    {isSubmitting ? 'Sending...' : 'Send Message'}
  </span>
</button>

      </form>
    </div>
    </div> 
  )
}

export default Message